import { FormEvent, useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { AtSign, BadgeCheck, Building2, Check, Eye, EyeOff, KeyRound, Phone, ShieldAlert, ShieldCheck, User, UserCog } from 'lucide-react'

import { authService } from '../services/authService'
import { useAuthStore } from '../stores/authStore'
import { usePermissions } from '../hooks/usePermissions'
import { getSidebarTheme } from '../config/sidebarThemes'
import { Field } from '../components/FormLayout'
import { UserAvatar } from '../components/account/UserAvatar'
import { displayName as nameOf, roleName } from '../components/account/accountDisplay'
import { formatSchoolDate } from '../components/students/studentDisplay'
import SalaryAdvanceCard from '../components/SalaryAdvanceCard'
import MyPayslipsCard from '../components/MyPayslipsCard'

function Detail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">{icon}</span>
      <div className="min-w-0">
        <dt className="text-xs font-medium text-gray-500">{label}</dt>
        <dd className="break-words text-sm font-medium text-gray-900">{children}</dd>
      </div>
    </div>
  )
}

function PasswordInput({ name, autoComplete, minLength, value, onChange }: { name: string; autoComplete: string; minLength?: number; value: string; onChange: (value: string) => void }) {
  const [shown, setShown] = useState(false)
  return (
    <div className="relative mt-1">
      <input
        required
        type={shown ? 'text' : 'password'}
        name={name}
        autoComplete={autoComplete}
        minLength={minLength}
        value={value}
        onChange={event => onChange(event.target.value)}
        className="input pr-10"
      />
      <button
        type="button"
        onClick={() => setShown(current => !current)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 hover:text-gray-700"
      >
        {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

export default function Profile() {
  const user = useAuthStore(state => state.user)
  const updateUser = useAuthStore(state => state.updateUser)
  const { isStaff } = usePermissions()
  const { hash } = useLocation()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // The account menu's "My pay" links to #pay; the router does not scroll to it by itself.
  useEffect(() => {
    if (hash === '#pay') document.getElementById('pay')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash])

  if (!user) return null

  const name = nameOf(user)
  const theme = getSidebarTheme(user.user_type)
  const departments = (user.headed_departments ?? []).map(dept => dept.name).join(', ')
  const showPay = isStaff && !user.must_change_password
  const longEnough = next.length >= 8
  const matches = !!confirm && next === confirm

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setMessage('')
    if (next !== confirm) {
      setError('The new passwords do not match.')
      return
    }
    setSaving(true)
    try {
      await authService.changePassword({ old_password: current, new_password: next, new_password_confirm: next })
      updateUser({ must_change_password: false })
      setCurrent('')
      setNext('')
      setConfirm('')
      setMessage('Your password is changed. Use the new one next time you sign in.')
    } catch (failure: unknown) {
      const response = (failure as { response?: { data?: Record<string, unknown> } })?.response?.data
      setError(response ? Object.values(response).flat().join(' ') : 'The password could not be changed. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const passwordCard = (
    <form onSubmit={changePassword} className="card">
      <div className="card-titlebar">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-gray-900"><KeyRound className="h-4 w-4 text-primary-700" />Password</h2>
          <p className="mt-1 text-sm text-gray-500">Use at least 8 characters, and nothing you use on another site.</p>
        </div>
      </div>
      <div className="space-y-4 px-6 py-5">
        {message && <div role="status" className="flex items-start gap-2 rounded-md bg-green-50 p-3 text-sm text-green-800"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />{message}</div>}
        {error && <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        <Field label="Current password">
          <PasswordInput name="old_password" autoComplete="current-password" value={current} onChange={setCurrent} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="New password" hint={next ? (longEnough ? <span className="inline-flex items-center gap-1 text-green-700"><Check className="h-3.5 w-3.5" />Long enough</span> : `${8 - next.length} more character${8 - next.length === 1 ? '' : 's'} needed`) : 'At least 8 characters.'}>
            <PasswordInput name="new_password" autoComplete="new-password" minLength={8} value={next} onChange={setNext} />
          </Field>
          <Field label="Confirm new password" hint={confirm ? (matches ? <span className="inline-flex items-center gap-1 text-green-700"><Check className="h-3.5 w-3.5" />Passwords match</span> : <span className="text-red-700">Does not match yet</span>) : 'Type it again.'}>
            <PasswordInput name="confirm_password" autoComplete="new-password" minLength={8} value={confirm} onChange={setConfirm} />
          </Field>
        </div>
      </div>
      <div className="card-footer">
        <button disabled={saving || !current || !longEnough || !matches} className="btn btn-primary">
          {saving ? 'Changing password…' : 'Change password'}
        </button>
      </div>
    </form>
  )

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {user.must_change_password && (
        <div role="alert" className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">Choose your own password to continue</p>
            <p>You signed in with a temporary password. Change it below and the rest of the system opens.</p>
          </div>
        </div>
      )}

      <section className="card overflow-hidden">
        <div className="h-24" style={{ background: `linear-gradient(120deg, ${theme.deep}, ${theme.light})` }} aria-hidden />
        <div className="flex flex-wrap items-end justify-between gap-4 px-6 pb-5">
          <div className="-mt-10 flex min-w-0 items-end gap-4">
            <span className="rounded-full bg-white p-1 shadow-sm">
              <UserAvatar name={name} role={user.user_type} photo={user.profile_picture} size="xl" />
            </span>
            <div className="min-w-0 pb-1">
              <h1 className="truncate text-2xl font-bold tracking-tight text-gray-900">{name}</h1>
              <p className="text-sm text-gray-600">
                {roleName(user.user_type)}
                {departments && <> · Head of {departments}</>}
                <span className="text-gray-400"> · {theme.label}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pb-1 text-xs font-medium">
            {user.is_verified ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-green-700"><BadgeCheck className="h-3.5 w-3.5" />Verified account</span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-gray-600">Not yet verified</span>
            )}
            {user.date_joined && <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-600">Member since {formatSchoolDate(user.date_joined)}</span>}
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-5">
        <section className={`card lg:col-span-2 ${user.must_change_password ? 'lg:order-2' : ''}`}>
          <div className="card-titlebar">
            <div>
              <h2 className="font-semibold text-gray-900">Account details</h2>
              <p className="mt-1 text-sm text-gray-500">How the school knows you.</p>
            </div>
          </div>
          <dl className="divide-y divide-gray-100 px-6 py-1">
            <Detail icon={<User className="h-4 w-4" />} label="Full name">{name}</Detail>
            <Detail icon={<UserCog className="h-4 w-4" />} label="Username">{user.username}</Detail>
            <Detail icon={<AtSign className="h-4 w-4" />} label="Email">{user.email || <span className="font-normal text-gray-400">Not provided</span>}</Detail>
            <Detail icon={<Phone className="h-4 w-4" />} label="Phone">{user.phone_number || <span className="font-normal text-gray-400">Not provided</span>}</Detail>
            <Detail icon={<Building2 className="h-4 w-4" />} label="Role">{roleName(user.user_type)}{departments && `, head of ${departments}`}</Detail>
          </dl>
          <p className="border-t border-gray-100 px-6 py-3 text-xs text-gray-500">
            Something wrong here? Ask an administrator to correct your name, contacts or role.
          </p>
        </section>

        <div className={`lg:col-span-3 ${user.must_change_password ? 'lg:order-1' : ''}`}>{passwordCard}</div>
      </div>

      {showPay && (
        <section id="pay" className="scroll-mt-6 space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">My pay</h2>
            <p className="text-sm text-gray-500">Your payslips and salary advances.</p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <MyPayslipsCard />
            <SalaryAdvanceCard />
          </div>
        </section>
      )}
    </div>
  )
}
